import React from "react";
import { Button } from "../components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import {
  Plane,
  Users,
  BarChart3,
  Building,
  Calendar,
  MapPin,
} from "lucide-react";

// Componentes
import HeroSection from "../components/sections/HeroSection";
import StatsSection from "../components/sections/StatsSection";
import TimelineSection from "../components/sections/TimelineSection";
import InvestmentChart from "../components/charts/InvestmentChart";
import AircraftProductionChart from "../components/charts/AircraftProductionChart";
import PassengerChart from "../components/charts/PassengerChart";
import AccidentChart from "../components/charts/AccidentChart";

import aeroportosConcedidos from "../assets/img/mapa/aeroportos_concedidos.png";

import diagnosticoPdf from "../assets/diagnostico/diagnostivo2024.pdf";


// Dados da publicação AÉREA
const aviationData = {
  title: "Diagnóstico Logístico Aéreo",
  subtitle: "Análise e marcos do transporte aéreo no Brasil em 2024",
  year: "2024",

  // Estatísticas principais
  stats: [
    {
      label: "Investimento Público",
      value: 1.129,
      suffix: "Bilhões",
      prefix: "R$",
      description: "75% direcionados ao Sistema de Controle do Espaço Aéreo",
      color: "text-blue-600",
    },
    {
      label: "Passageiros Transportados",
      value: 118.9,
      suffix: "Milhões",
      description: "Segundo melhor ano da história da aviação brasileira",
      color: "text-green-600",
    },
    {
      label: "Produção de Aeronaves",
      value: 203,
      suffix: "Unidades",
      description: "Crescimento de 13,4% na produção total",
      color: "text-purple-600",
    },
    {
      label: "Aeroportos Concedidos",
      value: 59,
      suffix: "Terminais",
      description: "Mais de 90% do tráfego de passageiros no país",
      color: "text-orange-600",
    },
  ],

  // Marcos mensais do transporte aéreo
  timeline: [
    {
      month: "Janeiro",
      title: "Brasil promulga novo acordo aéreo com o Canadá",
      description:
        "Decreto nº 11.891 moderniza as bases regulatórias do transporte aéreo bilateral, revogando o antigo tratado de 1990.",
      icon: "🇨🇦",
    },
    {
      month: "Fevereiro",
      title: "Passagens internacionais batem recorde histórico",
      description:
        "ANAC registra 2,04 milhões de passageiros em voos internacionais, o maior volume já observado em fevereiro.",
      icon: "📈",
    },
    {
      month: "Março",
      title: "Previsão de R$ 4,5 bilhões em investimentos aeroportuários",
      description:
        "AENA anuncia R$ 2 bilhões para Congonhas. Total de R$ 4,5 bilhões previstos para 11 aeroportos em três anos.",
      icon: "💰",
    },
    {
      month: "Abril",
      title: "ANAC publica diretriz sobre interferências do 5G",
      description:
        "Diretriz de Aeronavegabilidade determina ações preventivas sobre riscos de interferência em sistemas de rádioaltímetro.",
      icon: "📡",
    },
    {
      month: "Maio",
      title: "Brasil regulamenta compensações de carbono via CORSIA",
      description:
        "Resolução ANAC nº 743 institui regras nacionais de monitoramento e compensação de emissões de CO₂ em voos internacionais.",
      icon: "🌱",
    },
    {
      month: "Junho",
      title: "Chamamento para sandbox regulatório de vertiportos",
      description:
        "ANAC abre edital para projetos-piloto de vertiportos, testando soluções para veículos elétricos de pouso vertical (eVTOLs).",
      icon: "🚁",
    },
    {
      month: "Julho",
      title: "Lei federal cria marco para mobilidade sustentável",
      description:
        "Lei nº 14.946/2024 estabelece princípios para políticas de mobilidade de baixo carbono e captura de CO₂.",
      icon: "⚖️",
    },
    {
      month: "Agosto",
      title: "ANAC revoga limitação de voos em Guarulhos",
      description:
        "Medida que restringia operações em GRU foi retirada após cumprimento das exigências operacionais pela concessionária.",
      icon: "✈️",
    },
    {
      month: "Setembro",
      title: "Programa TransplantAR para transporte de órgãos",
      description:
        "São Paulo lança programa usando aeronaves privadas para transporte gratuito de órgãos e equipes médicas.",
      icon: "🏥",
    },
    {
      month: "Outubro",
      title: "Setor aéreo tem melhor outubro da história",
      description:
        "Movimentação atinge recorde com 10,4 milhões de passageiros, marcando o melhor outubro da série histórica.",
      icon: "🏆",
    },
    {
      month: "Novembro",
      title: "Advanced Air Mobility Conference 2024",
      description:
        "ANAC promove evento sobre certificação de eVTOLs, vertiportos e o novo ecossistema de mobilidade aérea avançada.",
      icon: "🚀",
    },
    {
      month: "Dezembro",
      title: "Brasil fecha 2024 com 118 milhões de passageiros",
      description:
        "Setor aéreo alcança segundo melhor desempenho da história, superando todos os anos pós-pandemia.",
      icon: "📊",
    },
  ],
};

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <HeroSection data={aviationData} />

      <StatsSection stats={aviationData.stats} />

      <TimelineSection timeline={aviationData.timeline} />

      {/* Seção de Investimentos */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Evolução dos Investimentos
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Análise da evolução dos investimentos públicos no setor aeroviário
              brasileiro
            </p>
          </div>
          <InvestmentChart />
        </div>
      </section>

      {/* Seção de Produção de Aeronaves */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Produção de Aeronaves
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Evolução da produção de aeronaves comerciais e executivas no
              Brasil
            </p>
          </div>
          <AircraftProductionChart />
        </div>
      </section>

      {/* Seção de Transporte de Passageiros */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Transporte de Passageiros
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Movimentação de passageiros no mercado doméstico e internacional
            </p>
          </div>
          <PassengerChart />
        </div>
      </section>

      {/* Seção de Acidentes Aéreos */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <AccidentChart />
        </div>
      </section>

      {/* Seção Infraestrutura Aeroportuária */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Infraestrutura Aeroportuária
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Distribuição dos aeroportos civis públicos e concedidos no Brasil
            </p>
          </div>

          <div className="flex justify-center mb-16">
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={aeroportosConcedidos}
              />
              <img
                src={aeroportosConcedidos}
                alt="Mapa da Infraestrutura Aeroportuária no Brasil"
                className="rounded-lg shadow-xl w-full max-w-6xl h-auto border-4 border-white"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>

          <div className="mt-16 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Panorama da Infraestrutura Aeroportuária
              </h3>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/60 p-6 rounded-lg">
                <div className="text-4xl font-bold text-blue-600 mb-2">505</div>
                <p className="text-gray-600 font-semibold mb-2">
                  Total de Aeródromos
                </p>
                <p className="text-sm text-gray-600">
                  446 sob administração pública e 59 concedidos à iniciativa
                  privada
                </p>
              </div>
              <div className="bg-white/60 p-6 rounded-lg">
                <div className="text-4xl font-bold text-green-600 mb-2">
                  90%
                </div>
                <p className="text-gray-600 font-semibold mb-2">
                  Tráfego de Passageiros
                </p>
                <p className="text-sm text-gray-600">
                  dos passageiros circulam por aeroportos concedidos à
                  iniciativa privada
                </p>
              </div>
              <div className="bg-white/60 p-6 rounded-lg">
                <div className="text-4xl font-bold text-purple-600 mb-2">
                  98%
                </div>
                <p className="text-gray-600 font-semibold mb-2">
                  Transporte de Cargas
                </p>
                <p className="text-sm text-gray-600">
                  das cargas aéreas passam por terminais sob gestão privada
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Distribuição Regional
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    São Paulo
                  </div>
                  <p className="text-gray-600">
                    lidera com 68 aeródromos públicos, seguido por Minas Gerais
                    (63) e Rio Grande do Sul (44)
                  </p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    Pará
                  </div>
                  <p className="text-gray-600">
                    possui o maior número de aeroportos concedidos (5),
                    fortalecendo a conectividade regional
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Acesse o Diagnóstico Completo
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Baixe o documento completo e tenha acesso a todos os dados, análises
            detalhadas e insights estratégicos sobre o setor aeroviário
            brasileiro.
          </p>
    <a
  href={diagnosticoPdf}
  download="diagnostico2024.pdf"
  rel="noopener noreferrer"
>
  <Button
    size="lg"
    className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 text-lg group"
  >
    <Download className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
    Download do Diagnóstico
    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
  </Button>
</a>
        </div>
      </section>

      {/* Footer */}
      {/* <footer className="py-12 bg-[#193e73] text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Plane className="w-6 h-6" />
                INFRA S.A.
              </h3>
              <p className="text-gray-400 mb-4">
                Observatório Nacional de Transporte e Logística
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <MapPin className="w-4 h-4" />
                Brasília, DF - Brasil
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Recursos</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Diagnósticos
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Relatórios
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Dados Abertos
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Metodologia
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Fale Conosco
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Imprensa
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Parcerias
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.infrasa.gov.br"
                    className="hover:text-white transition-colors"
                  >
                    Site Oficial
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 INFRA S.A. - Todos os direitos reservados.</p>
          </div>
        </div>
      </footer> */}
    </div>
  );
}
